import React, { useState } from 'react';
import {
  Drawer,
  IconButton,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Menu as MenuIconDefault, Close as CloseIconMui } from '@mui/icons-material';
import SectionMenuIcon from 'frontend/shared/SectionMenuIcon';

export interface MenuDrawerItemData {
  id: string | number;
  label: string;
  icon?: string | null;
  iconSrc?: string | null;
  url?: string;
  target?: '_self' | '_blank';
}

export interface MenuDrawerProps {
  items?: MenuDrawerItemData[];
  iconColor?: string;
  // The element the drawer portals into (instead of the default document.body).
  // Passing the container that wraps header+hero keeps the drawer confined to
  // that block, reaching down to the end of the hero, with its own scroll.
  portalContainer?: HTMLElement | null;
  triggerClassName?: string;
  triggerIcon?: React.ReactNode;
  triggerLabel?: string;
  /** Controlled open state (e.g. Ecommerce "Más..." opens the same drawer). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Desktop side panel even when the viewport is below the md breakpoint. */
  sidePanel?: boolean;
}

interface MenuDrawerItemProps {
  item: MenuDrawerItemData;
  color: string;
  onClick: () => void;
}

// Mirrors NavBarMobile.js's NavBarItem: real `href`/`target` navigation, and an
// icon slot that's always reserved (empty when the item has none) so labels stay aligned.
const MenuDrawerItem: React.FC<MenuDrawerItemProps> = ({ item, color, onClick }) => (
  <ListItem
    component='a'
    href={item.url || '#'}
    onClick={
      item.url
        ? onClick
        : (event) => {
            event.preventDefault();
            onClick();
          }
    }
    sx={{
      py: 1.5,
      px: 2,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    }}
    target={item.target || '_self'}
  >
    <ListItemIcon sx={{ minWidth: '40px' }}>
      {(item.iconSrc || item.icon) && (
        <SectionMenuIcon codigo={item.icon} color={color} src={item.iconSrc} />
      )}
    </ListItemIcon>
    <ListItemText
      primary={item.label}
      primaryTypographyProps={{
        sx: { fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' },
      }}
    />
  </ListItem>
);

const MenuDrawer: React.FC<MenuDrawerProps> = ({
  items,
  iconColor = '#54a5e8',
  portalContainer,
  triggerClassName,
  triggerIcon,
  triggerLabel = 'Abrir menú',
  open: openProp,
  onOpenChange,
  sidePanel = false,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const setOpen = (next: boolean): void => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isBottomSheet = !sidePanel && isMobile;
  const menuItems = items ?? [];
  const drawerAnchor = isBottomSheet ? 'bottom' : 'right';

  const handleClose = (): void => setOpen(false);

  return (
    <>
      <button
        aria-expanded={open}
        aria-label={triggerLabel}
        className={triggerClassName}
        onClick={() => setOpen(true)}
        type='button'
      >
        {triggerIcon || <MenuIconDefault />}
      </button>

      <Drawer
        BackdropProps={{ sx: { position: 'absolute' } }}
        ModalProps={{
          container: portalContainer || undefined,
          keepMounted: false,
          disableScrollLock: true,
        }}
        PaperProps={{
          sx: {
            position: 'absolute',
            ...(isBottomSheet
              ? { borderTopLeftRadius: 16, borderTopRightRadius: 16 }
              : { borderTopLeftRadius: 16, borderBottomLeftRadius: 16 }),
          },
        }}
        anchor={drawerAnchor}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        onClose={handleClose}
        open={open}
      >
        <Box
          sx={{
            width: isBottomSheet ? 'auto' : 320,
            height: isBottomSheet ? 'auto' : '100%',
            maxHeight: isBottomSheet ? '70vh' : '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ fontSize: '1.125rem', fontWeight: 600, color: 'text.primary' }}>Menú</Box>
            <IconButton onClick={handleClose} size='small' sx={{ color: 'text.secondary' }}>
              <CloseIconMui />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <List sx={{ py: 0 }}>
              {menuItems.map((item) => (
                <MenuDrawerItem color={iconColor} item={item} key={item.id} onClick={handleClose} />
              ))}
            </List>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default MenuDrawer;
